import { useCallback, useEffect, useState } from 'react'
import { Bell, X } from 'lucide-react'

import {
    canUsePush,
    getCurrentPermission,
    isPushSupported,
    isSubscribed,
    subscribePush,
} from '../services/push'

/**
 * NotificationsPrompt — non-intrusive opt-in for Web Push notifications.
 *
 * Appears once per 30 days when ALL of:
 *   - browser supports the SW + Push API stack
 *   - backend has VAPID configured (probed via 503 from /push/vapid-public-key)
 *   - user hasn't already subscribed
 *   - notification permission is still 'default' (not 'granted'/'denied')
 *
 * Show delay = 30s — gives InstallPrompt (8s) the floor first, and avoids
 * surfacing two prompts at the same time.
 *
 * On accept → calls subscribePush() which prompts the OS-level permission
 * dialog and POSTs the resulting subscription to backend. On any error →
 * silently dismisses (no scary modal — push is enhancement, not core).
 */

const DISMISS_KEY = 'onboardix_push_prompt_dismissed_until'
const DISMISS_DAYS = 30
const SHOW_DELAY_MS = 30_000

function isDismissed() {
    try {
        const until = Number(localStorage.getItem(DISMISS_KEY) || '0')
        return until && Date.now() < until
    } catch {
        return false
    }
}

export default function NotificationsPrompt() {
    const [visible, setVisible] = useState(false)
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        let cancelled = false
        if (!isPushSupported()) return undefined
        if (getCurrentPermission() !== 'default') return undefined
        if (isDismissed()) return undefined

        const t = setTimeout(async () => {
            if (cancelled) return
            const [usable, alreadySubbed] = await Promise.all([canUsePush(), isSubscribed()])
            if (cancelled) return
            if (!usable || alreadySubbed) return
            // Re-check permission — user may have granted via another tab.
            if (getCurrentPermission() !== 'default') return
            setVisible(true)
        }, SHOW_DELAY_MS)

        return () => {
            cancelled = true
            clearTimeout(t)
        }
    }, [])

    const handleEnable = useCallback(async () => {
        setBusy(true)
        try {
            await subscribePush()
            setVisible(false)
        } catch (err) {
            // 'permission-denied' / 'subscribe-failed' / 'vapid-unavailable'
            // — close the card silently; user already saw OS-level prompt or
            // there's nothing to retry from here.
            if (typeof window !== 'undefined' && window.console) {
                console.warn('[push] subscribe failed:', err?.code || err)
            }
            handleDismiss()
        } finally {
            setBusy(false)
        }
    }, [])

    const handleDismiss = useCallback(() => {
        try {
            const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000
            localStorage.setItem(DISMISS_KEY, String(until))
        } catch { /* noop */ }
        setVisible(false)
    }, [])

    if (!visible) return null

    const cardStyle = {
        position: 'fixed',
        left: '50%',
        bottom: 'max(20px, env(safe-area-inset-bottom))',
        transform: 'translateX(-50%)',
        zIndex: 99997, // под InstallPrompt (99998), чтобы не перекрывать в редком одновременном показе
        background: 'color-mix(in srgb, var(--color-shell-surface) 96%, transparent)',
        color: 'var(--color-shell-text)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        padding: '14px 18px',
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '0.875rem',
        maxWidth: 'min(420px, calc(100vw - 24px))',
        border: '1px solid rgba(255,255,255,0.08)',
        animation: 'onboardix-pwa-slideup 0.35s ease',
    }
    const btnPrimary = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 14px',
        borderRadius: 'var(--radius-md)',
        border: 'none',
        background: 'var(--color-accent)',
        color: 'var(--color-text-inverse)',
        fontWeight: 600,
        fontSize: '0.812rem',
        cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.7 : 1,
        whiteSpace: 'nowrap',
    }
    const btnDismiss = {
        background: 'transparent',
        border: 'none',
        color: 'var(--color-text-muted)',
        cursor: 'pointer',
        padding: 4,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--radius-sm)',
    }

    return (
        <div role="dialog" aria-label="Включить уведомления" style={cardStyle}>
            <Bell size={22} color='var(--color-accent)' style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, lineHeight: 1.4 }}>
                Включите уведомления — будем сообщать о новых откликах, упоминаниях и задачах.
            </div>
            <button type="button" style={btnPrimary} onClick={handleEnable} disabled={busy}>
                {busy ? 'Подключаем…' : 'Включить'}
            </button>
            <button
                type="button"
                style={btnDismiss}
                onClick={handleDismiss}
                aria-label="Закрыть"
                title="Не показывать 30 дней"
            >
                <X size={18} />
            </button>
        </div>
    )
}
